import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useContractReview,
  useSetContractReviewStatus,
  useAddContractReviewComment,
  useContractReviewRealtime,
} from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { ReviewThread } from '@/components/contract-reviews/ReviewThread'
import { DocumentLinks } from '@/components/contract-reviews/DocumentLinks'
import { formatDate } from '@/lib/utils'
import { explainError } from '@/lib/explain-error'
import type { ContractReviewStatus } from '@/types'

/** The statuses after which nothing about the review may change. Matches TERMINAL on the server. */
const SETTLED: ContractReviewStatus[] = ['Signed', 'Declined', 'Withdrawn']

/**
 * One contract review: where it has got to, what was said, and what you can do next.
 *
 * THE STATUS CONTROL RENDERS FROM THE SERVER, NOT FROM A LOCAL LIST. `available_transitions`
 * comes from the state machine, which already knows the current status, this viewer's role and
 * whether the preconditions are met. Rebuilding that judgement in the browser would mean two
 * implementations that must agree, and the one on screen would be the one that drifted.
 *
 * So the buttons are whatever the server offers, and a refusal is shown as the server worded it.
 */

/** Transitions where the whole point is what was said, so the comment box opens focused. */
const EXPECTS_COMMENT: ContractReviewStatus[] = ['Changes proposed', 'Declined']

export default function ContractReviewDetail() {
  const { id } = useParams<{ id: string }>()
  const { currentUser, impersonatedUser } = useUser()
  const me = impersonatedUser?.id || currentUser?.id

  const { data: review, isLoading, error } = useContractReview(id)
  const setStatus = useSetContractReviewStatus()
  const addComment = useAddContractReviewComment()

  const [pending, setPending] = useState<ContractReviewStatus | null>(null)
  const [note, setNote] = useState('')
  const [comment, setComment] = useState('')
  const [problem, setProblem] = useState<string | null>(null)

  useDocumentTitle(review?.title ?? 'Contract review')
  useContractReviewRealtime()

  const submitStatus = async () => {
    if (!pending || !id) return
    setProblem(null)
    try {
      await setStatus.mutateAsync({ id, status: pending, comment: note.trim() || undefined })
      setPending(null)
      setNote('')
    } catch (err) {
      // The server's refusals are written for the person who tried ("Only the reviewer this
      // contract was sent to can sign it"), so show them rather than a generic message.
      setProblem(explainError(err, 'That change could not be made'))
    }
  }

  const submitComment = async () => {
    if (!comment.trim() || !id) return
    setProblem(null)
    try {
      await addComment.mutateAsync({ id, body: comment.trim(), comment_type: 'note' })
      setComment('')
    } catch (err) {
      setProblem(explainError(err, 'The comment could not be added'))
    }
  }

  if (isLoading) {
    return (
      <main className="p-6">
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          Loading this review…
        </p>
      </main>
    )
  }

  if (error || !review) {
    return (
      <main className="p-6 space-y-4">
        <Link to="/contract-reviews" className="text-sm text-primary underline underline-offset-4">
          All contract reviews
        </Link>
        <Alert variant="destructive">
          <AlertDescription>
            {/* This is now reached only when a review genuinely does not exist, or was
                deleted. Not being on a review no longer lands here: since the progress split
                that returns the review with its terms withheld. */}
            {explainError(error, 'That contract review could not be found')}
          </AlertDescription>
        </Alert>
      </main>
    )
  }

  const waitingLabel =
    review.waiting_on === 'reviewer'
      ? review.reviewer_name || 'the reviewer'
      : review.waiting_on === 'requester'
        ? review.requested_by_name || 'the requester'
        : null

  return (
    <main className="p-6 space-y-4">
      <section aria-labelledby="review-heading" className="space-y-4">
        <Link
          to="/contract-reviews"
          className="inline-flex items-center gap-1.5 text-sm text-primary underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All contract reviews
        </Link>

        <div>
          <h1 id="review-heading" className="text-2xl font-semibold">
            {review.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            CR-{review.review_number} ·{' '}
            <Link
              to={`/accounts/${review.account_id}`}
              className="text-primary underline underline-offset-4"
            >
              {review.account_name || review.account_id}
            </Link>
            {review.opportunity_id && (
              <>
                {' · '}
                <Link
                  to={`/opportunities/${review.opportunity_id}`}
                  className="text-primary underline underline-offset-4"
                >
                  {review.opportunity_name || 'Opportunity'}
                </Link>
              </>
            )}
          </p>
        </div>

        {/* The state of play, in one line of plain words. role=status so a screen reader is told
            when it changes after a transition rather than having to go looking. */}
        <Card>
          <CardContent className="py-3">
            <p className="text-sm" role="status">
              <span className="font-semibold">{review.status}</span>
              {waitingLabel && <> · waiting on {waitingLabel}</>}
              {review.rounds > 0 && (
                <>
                  {' · '}
                  {review.rounds} {review.rounds === 1 ? 'round' : 'rounds'} of changes
                </>
              )}
              {review.signed_at && <> · signed {formatDate(review.signed_at)}</>}
            </p>
          </CardContent>
        </Card>

        {problem && (
          <Alert variant="destructive">
            {/* Alert already carries role="alert", so a refusal is announced when it appears.
                Adding another here would nest one alert inside another. */}
            <AlertDescription>{problem}</AlertDescription>
          </Alert>
        )}

        {/*
          Fails CLOSED. The terms are hidden unless the server explicitly says this viewer may
          see them, rather than shown unless it says they may not. An absent flag then means a
          hidden thread, which is a visible, reportable bug; the other way round it would mean
          contract terms rendered to someone who should not have them, which is silent.
        */}
        {review.can_see_terms !== true ? (
          /*
           * Said plainly rather than shown as an empty panel.
           *
           * Progress is open to the team; the terms are not. Someone looking at a contract they
           * are not on should understand immediately that there is nothing broken here and that
           * the gap is deliberate, otherwise the obvious conclusion is that the page failed to
           * load.
           */
          <Card>
            <CardContent className="py-4">
              <h2 className="text-sm font-semibold">Documents and discussion</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Only {review.requested_by_name || 'the person who raised this'} and{' '}
                {review.reviewer_name || 'the reviewer'} can see the contract and what was redlined.
                You can follow its progress here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-4">
              {/* Mirrors the server's refuseIfSettled: a finished negotiation is a record. */}
              <DocumentLinks review={review} editable={!SETTLED.includes(review.status)} />
            </CardContent>
          </Card>
        )}

        {/* What you can do next */}
        {review.available_transitions.length > 0 && (
          <Card>
            <CardContent className="py-4 space-y-3">
              <h2 className="text-sm font-semibold">Move this on</h2>
              {!pending && (
                <div className="flex flex-wrap gap-2">
                  {review.available_transitions.map((next) => (
                    <Button
                      key={next}
                      variant={next === 'Signed' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setPending(next)
                        setNote('')
                        setProblem(null)
                      }}
                      // Named in full: "Agreed" alone is ambiguous out of context, and a
                      // screen-reader user hears the buttons as a list.
                      aria-label={`Move this review to ${next}`}
                    >
                      {next}
                    </Button>
                  ))}
                </div>
              )}

              {pending && (
                <div className="space-y-2">
                  <label htmlFor="transition-note" className="text-sm font-medium block">
                    {EXPECTS_COMMENT.includes(pending)
                      ? `What are you proposing? This becomes the ${pending === 'Declined' ? 'reason' : 'redline'}.`
                      : `Add a note with this change (optional)`}
                  </label>
                  <Textarea
                    id="transition-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={4}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={submitStatus} disabled={setStatus.isPending}>
                      {setStatus.isPending ? 'Saving…' : `Confirm: ${pending}`}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPending(null)
                        setNote('')
                      }}
                      disabled={setStatus.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* The negotiation */}
        {review.can_see_terms === true && (
          <Card>
            <CardContent className="py-4 space-y-3">
              <h2 className="text-sm font-semibold">
                Thread
                <span className="font-normal text-muted-foreground">
                  {' '}
                  ({review.comments.length})
                </span>
              </h2>
              <ReviewThread comments={review.comments} currentUserId={me} />

              <div className="border-t pt-3 space-y-2">
                <label htmlFor="new-comment" className="text-sm font-medium block">
                  Add a note
                </label>
                <Textarea
                  id="new-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  placeholder="A note on the thread, without changing the status."
                />
                <Button
                  size="sm"
                  onClick={submitComment}
                  disabled={!comment.trim() || addComment.isPending}
                >
                  {addComment.isPending ? 'Adding…' : 'Add note'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </section>
    </main>
  )
}
